import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Facebook, Menu, X, ArrowUpRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import LanguageToggle from "./LanguageToggle";
import LanguageToggleMobile from "./LanguageToggleMobile";
import { translations } from "../i18n/translation";
import { facebooklink } from "./SocialMediaLink";

const navItems = [
  { key: "home", target: "#hero" },
  { key: "about", target: "#intro" },
  { key: "approach", target: "#about" },
  { key: "services", target: "#services" },
  { key: "download", path: "/free-download" },
  { key: "blog", target: "#blog" },
];

const menuVariants = {
  closed: {
    opacity: 0,
    y: -18,
    transition: {
      duration: 0.2,
      when: "afterChildren",
      staggerChildren: 0.04,
      staggerDirection: -1,
    },
  },
  open: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1],
      when: "beforeChildren",
      staggerChildren: 0.07,
      delayChildren: 0.06,
    },
  },
};

const menuItemVariants = {
  closed: {
    opacity: 0,
    x: -16,
  },
  open: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const MobileMenuDivider = () => (
  <div className="flex items-center justify-center" aria-hidden="true">
    <div className="h-px flex-1 bg-gradient-to-r from-transparent to-neonYellow/40" />

    <div className="mx-4 flex items-center gap-1.5">
      {[0, 1, 2].map((dot) => (
        <motion.span
          key={dot}
          className="h-1 w-1 rounded-full bg-neonYellow"
          animate={{
            opacity: [0.35, 1, 0.35],
            scale: [0.8, 1.2, 0.8],
          }}
          transition={{
            duration: 1.8,
            repeat: Infinity,
            delay: dot * 0.16,
          }}
        />
      ))}
    </div>

    <div className="h-px flex-1 bg-gradient-to-l from-transparent to-neonYellow/40" />
  </div>
);

const MobileFooterSection = ({ onBookSession }) => (
  <motion.div
    variants={menuItemVariants}
    className="mt-2 border-0 border-white/10 px-5 pb-8 pt-6 text-center"
  >
    <MobileMenuDivider />

    <div className="mt-6 space-y-2">
      <h4 className="text-sm font-semibold tracking-wide text-white">
        Let&apos;s connect
      </h4>

      {/* <p className="mx-auto max-w-xs text-xs leading-5 text-gray-400">
        Follow for practical career insights, useful tips and real stories.
      </p> */}
    </div>

    <div className="mt-5 flex justify-center">
      <motion.a
        href={facebooklink}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Follow us on Facebook"
        whileHover={{ scale: 1.06, y: -2 }}
        whileTap={{ scale: 0.94 }}
        className="
          flex h-11 w-11 items-center justify-center rounded-full
          border border-white/15 bg-white/[0.04] text-gray-300
          transition-colors duration-300
          hover:border-neonYellow/70 hover:bg-neonYellow/10
          hover:text-neonYellow
          focus:outline-none focus-visible:ring-2
          focus-visible:ring-neonYellow
        "
      >
        <Facebook size={18} />
      </motion.a>
    </div>

    <motion.button
      type="button"
      onClick={onBookSession}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.97 }}
      className="
        group mt-6 inline-flex min-h-11 items-center justify-center
        gap-2 rounded-full bg-neonYellow px-6 py-3
        text-sm font-semibold text-navy shadow-lg
        shadow-neonYellow/10 transition-shadow duration-300
        hover:shadow-neonYellow/25
        focus:outline-none focus-visible:ring-2
        focus-visible:ring-neonYellow focus-visible:ring-offset-2
        focus-visible:ring-offset-dry
      "
    >
      Book a Session
      <ArrowUpRight
        size={16}
        className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
      />
    </motion.button>
  </motion.div>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const lang = useSelector((state) => state.language.lang);
  const t = translations[lang] || translations.en;

  const closeMenu = () => setOpen(false);

  useEffect(() => {
    closeMenu();
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!open) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [open]);

  const scrollToSection = (target) => {
    closeMenu();

    if (location.pathname !== "/") {
      navigate(`/${target}`);
      return;
    }

    const element = document.querySelector(target);

    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleNavItem = (item) => {
    closeMenu();

    if (item.path) {
      navigate(item.path);
      return;
    }

    scrollToSection(item.target);
  };

  const goHome = () => {
    closeMenu();

    if (location.pathname !== "/") {
      navigate("/#hero");
      return;
    }

    const hero = document.querySelector("#hero");

    if (hero) {
      hero.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const isItemActive = (item) => {
    if (item.path) {
      return location.pathname === item.path;
    }

    if (item.key === "home") {
      return (
        location.pathname === "/" &&
        (!location.hash || location.hash === "#hero")
      );
    }

    return location.pathname === "/" && location.hash === item.target;
  };

  return (
    <>
      <nav className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-dry shadow-lg shadow-black/5 backdrop-blur-xl">
        <div className="mx-auto flex h-[80px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <motion.button
            type="button"
            onClick={goHome}
            aria-label="Go to homepage"
            whileTap={{ scale: 0.96 }}
            className="relative z-10 flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-neonYellow"
          >
            <img
              src="/images/logo_black.png"
              alt="Tiffany Trâm Bùi"
              className="h-auto w-[100px] object-contain"
            />
          </motion.button>

          {/* Desktop navigation */}
          <div className="hidden items-center gap-1 md:flex">
            {navItems.map((item) => {
              const active = isItemActive(item);

              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => handleNavItem(item)}
                  className={`
                    group relative rounded-lg px-4 py-2.5
                    text-sm font-medium transition-colors duration-300
                    focus:outline-none focus-visible:ring-2
                    focus-visible:ring-neonYellow
                    ${
                      active
                        ? "text-neonYellow font-semibold"
                        : "text-gray-300 hover:text-white"
                    }
                  `}
                >
                  {t[item.key]}

                  <span
                    className={`
                      absolute bottom-1 left-1/2 h-0.5 -translate-x-1/2
                      rounded-full bg-neonYellow transition-all duration-300
                      ${
                        active
                          ? "w-5 opacity-100"
                          : "w-0 opacity-0 group-hover:w-5 group-hover:opacity-100"
                      }
                    `}
                  />
                </button>
              );
            })}

            <div className="ml-3 border-l border-white/10 pl-4">
              <LanguageToggle />
            </div>
          </div>

          {/* Mobile controls */}
          <div className="relative z-10 flex items-center gap-2 md:hidden">
            <LanguageToggleMobile />

            <motion.button
              type="button"
              onClick={() => setOpen((current) => !current)}
              whileTap={{ scale: 0.9 }}
              aria-label={
                open ? "Close navigation menu" : "Open navigation menu"
              }
              aria-expanded={open}
              aria-controls="mobile-navigation"
              className="
                relative flex h-11 w-11 items-center justify-center
                rounded-full border border-white/10 bg-white/[0.04]
                text-gray-200 transition-colors duration-300
                hover:border-neonYellow/50 hover:text-neonYellow
                focus:outline-none focus-visible:ring-2
                focus-visible:ring-neonYellow
              "
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={open ? "close" : "menu"}
                  initial={{ opacity: 0, rotate: -90, scale: 0.7 }}
                  animate={{ opacity: 1, rotate: 0, scale: 1 }}
                  exit={{ opacity: 0, rotate: 90, scale: 0.7 }}
                  transition={{ duration: 0.18 }}
                  className="absolute"
                >
                  {open ? <X size={23} /> : <Menu size={23} />}
                </motion.span>
              </AnimatePresence>
            </motion.button>
          </div>
        </div>

        {/* Mobile navigation */}
        <AnimatePresence>
          {open && (
            <motion.div
              id="mobile-navigation"
              variants={menuVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="
                absolute left-0 top-full w-full overflow-y-auto h-[calc(100dvh-72px)]
                border-t border-white/30 bg-dry shadow-2xl
                shadow-black/30 backdrop-blur-xl md:hidden
              "
              style={{
                maxHeight: "calc(100dvh - 72px)",
              }}
            >
              <div className="mx-auto w-full max-w-lg px-4 pb-2 pt-4">
                <div className="flex flex-col gap-1">
                  {navItems.map((item, index) => {
                    const active = isItemActive(item);

                    return (
                      <motion.button
                        key={item.key}
                        type="button"
                        variants={menuItemVariants}
                        onClick={() => handleNavItem(item)}
                        className={`
                          group relative flex min-h-12 w-full items-center
                          justify-between overflow-hidden rounded-xl px-4
                          text-left transition-colors duration-300
                          focus:outline-none focus-visible:ring-2
                          focus-visible:ring-neonYellow
                          ${
                            active
                              ? "bg-neonYellow/10 text-neonYellow"
                              : "text-gray-200 hover:bg-white/[0.05] hover:text-white"
                          }
                        `}
                      >
                        <span className="flex items-center gap-4">
                          <span
                            className={`
                              text-[10px] font-semibold tracking-[0.15em]
                              ${active ? "text-neonYellow" : "text-gray-500"}
                            `}
                          >
                            {String(index + 1).padStart(2, "0")}
                          </span>

                          <span className="text-base font-medium">
                            {t[item.key]}
                          </span>
                        </span>

                        <ArrowUpRight
                          size={17}
                          className={`
                            transition-all duration-300
                            ${
                              active
                                ? "opacity-100"
                                : "translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-70"
                            }
                          `}
                        />

                        {active && (
                          <motion.span
                            layoutId="mobile-active-indicator"
                            className="absolute bottom-3 left-0 top-3 w-0.5 rounded-full bg-neonYellow"
                            transition={{
                              type: "spring",
                              stiffness: 350,
                              damping: 30,
                            }}
                          />
                        )}
                      </motion.button>
                    );
                  })}
                </div>

                <MobileFooterSection
                  onBookSession={() => {
                    closeMenu();
                    navigate("/achievers");
                  }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Mobile background overlay */}
      <AnimatePresence>
        {open && (
          <motion.button
            type="button"
            aria-label="Close navigation menu"
            onClick={closeMenu}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 cursor-default bg-black/55 backdrop-blur-[2px] md:hidden"
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;

// import React, { useState } from "react";
// import { Facebook, Menu, X } from "lucide-react";
// import { useNavigate, useLocation } from "react-router-dom";
// import LanguageToggle from "./LanguageToggle";
// import LanguageToggleMobile from "./LanguageToggleMobile";

// import { useSelector } from "react-redux";
// import { translations } from "../i18n/translation";
// import { facebooklink } from "./SocialMediaLink";

// const Navbar = () => {
//   const [open, setOpen] = useState(false);

//   const navigate = useNavigate();
//   const location = useLocation();

//   const lang = useSelector((state) => state.language.lang);

//   const t = translations[lang];

//   const WriteSection = () => {
//     return (
//       <div className="px-6 pb-8 text-center border-t border-gray-800 mt-0 space-y-5">
//         {/* Divider */}
//         <MobileMenuDivider />
//         <div className="flex justify-center gap-4">
//           <a
//             href={facebooklink}
//             target="_blank"
//             rel="noreferrer"
//             className="w-10 h-10 flex items-center justify-center
//                      rounded-full border border-gray-700
//                      hover:border-neonYellow
//                      hover:text-neonYellow transition"
//           >
//             <Facebook size={18} />
//           </a>
//         </div>
//         {/* CTA TEXT */}
//         <div className="space-y-2">
//           <h4 className="text-sm font-semibold text-white tracking-wide">
//             Let’s Connect
//           </h4>
//           <p className="text-xs text-gray-400">
//             Follow for insights, career tips & real stories
//           </p>
//         </div>

//         {/* SOCIAL */}

//         {/* OPTIONAL CTA BUTTON */}
//         <button
//           onClick={() => {
//             setOpen(false);
//             navigate("/achievers");
//           }}
//           className="mt-2 text-xs px-4 py-2 rounded-full
//                    bg-neonYellow  font-medium
//                    hover:opacity-90 transition"
//         >
//           <span className="text-navy animate-neonFlicker">Book a Session</span>
//         </button>
//       </div>
//     );
//   };

//   const MobileMenuDivider = () => (
//     <div className="relative my-3 flex items-center justify-center">
//       {/* left dotted line */}
//       <div className="flex-1 border-t border-dashed border-neonYellow/50"></div>

//       {/* center dots */}
//       <div className="mx-4 flex items-center gap-1">
//         <span className="w-1 h-1 bg-neonYellow rounded-full"></span>
//         <span className="w-1 h-1 bg-neonYellow rounded-full"></span>
//         <span className="w-1 h-1 bg-neonYellow rounded-full"></span>
//         <span className="w-1 h-1 bg-neonYellow rounded-full"></span>
//         <span className="w-1 h-1 bg-neonYellow rounded-full"></span>
//       </div>

//       {/* right dotted line */}
//       <div className="flex-1 border-t border-dashed border-gray-600/50"></div>
//     </div>
//   );

//   // Smooth scroll
//   // const scrollToSection = (id) => {
//   //   const el = document.querySelector(id);
//   //   if (!el) return;

//   //   setOpen(false); // close menu on mobile
//   //   el.scrollIntoView({ behavior: "smooth", block: "start" });
//   // };

//   const navItems = [
//     { key: "home", target: "#hero" },
//     { key: "about", target: "#intro" },
//     { key: "approach", target: "#about" },
//     { key: "services", target: "#services" },
//     { key: "download", path: "/free-download" },
//     { key: "blog", target: "#blog" },
//   ];

//   const scrollToSection = (id) => {
//     setOpen(false);

//     // If NOT on homepage → navigate there first
//     if (location.pathname !== "/") {
//       navigate("/" + id); // example: "/#services"
//       return;
//     }

//     // If already on homepage → scroll normally
//     const el = document.querySelector(id);
//     if (el) {
//       el.scrollIntoView({ behavior: "smooth", block: "start" });
//     }
//   };

//   const handleNavItem = (item) => {
//     setOpen(false);

//     // Normal page route
//     if (item.path) {
//       navigate(item.path);
//       return;
//     }

//     // Homepage section
//     scrollToSection(item.target);
//   };

//   const goHome = () => {
//     setOpen(false);

//     if (location.pathname !== "/") {
//       navigate("/#hero"); // 👈 go to homepage
//       return;
//     }

//     // If already on homepage → scroll to top
//     const el = document.querySelector("#hero");
//     if (el) {
//       el.scrollIntoView({ behavior: "smooth" });
//     }
//   };

//   return (
//     <nav className="fixed top-0 left-0 w-full bg-dry border-b border-gray-800 z-50">
//       <div className="max-w-7xl mx-auto flex items-center justify-between p-4">
//         {/* LOGO */}
//         <div className="flex items-center gap-x-2">
//           <img
//             src="/images/logo_black.png"
//             width={100}
//             className="cursor-pointer"
//             onClick={goHome}
//           />
//         </div>

//         {/* DESKTOP MENU */}
//         <div className="hidden md:flex space-x-8 text-sm font-medium">
//           {navItems.map((item) => (
//             <button
//               key={item.key}
//               type="button"
//               onClick={() => handleNavItem(item)}
//               className={`text-left transition hover:text-neonYellow hover:animate-neonFlicker ${
//                 item.path === location.pathname ? "text-neonYellow" : ""
//               }`}
//             >
//               {t[item.key]}
//             </button>
//           ))}

//           <LanguageToggle />
//         </div>

//         {/* MOBILE BURGER ICON */}
//         <div className="flex space-x-2 md:hidden">
//           <LanguageToggleMobile />

//           <button className=" text-gray-300" onClick={() => setOpen(!open)}>
//             {open ? <X size={26} /> : <Menu size={26} />}
//           </button>
//         </div>
//       </div>

//       {/* MOBILE DROPDOWN MENU */}

//       {open && (
//         <div className="flex flex-col gap-4 border-0 border-gray-800 bg-dry px-5 py-4 md:hidden">
//           {navItems.map((item) => (
//             <button
//               key={item.key}
//               type="button"
//               onClick={() => handleNavItem(item)}
//               className={`block w-full text-left transition hover:text-neonYellow ${
//                 item.path === location.pathname
//                   ? "text-neonYellow"
//                   : "text-white"
//               }`}
//             >
//               {t[item.key]}
//             </button>
//           ))}

//           <div className="w-full">
//             <WriteSection />
//           </div>
//         </div>
//       )}
//     </nav>
//   );
// };

// export default Navbar;
